// eslint-disable-next-line @heidi/rules/no-new-legacy-session-data -- pre-existing patients delegate importing only the two frozen linked-session key identities from /contracts (the rule anchors on the first import, hence the placement)
import { isViewingOtherSessionAtom, sessionQueryVersionAtom } from '@/atoms/sessionAtoms';
import { selectedReviewMemberAtom } from '@/atoms/teamAtoms';
import {
  invalidateSessionsQuery,
  invalidateTeamMemberSessionsQuery,
} from '@/hooks/query/useQueryCombinedSessions';
import { GET_INFINITE_SESSIONS_WITH_PAGINATION } from '@/hooks/query/useQuerySessionsWithPagination';
import { type PatientLinkedEventProps } from '@/models/analytics/features/patient';
import { postLinkSessionsToPatientProfile } from '@/pages/api/scribeAPIV2';
import { logger } from '@/utils/logger/logger';
import { sanitizeError } from '@/utils/logger/sanitizeError';
import { toast as designSystemToast } from '@heidi/design-system/toast';
import { useTranslations } from '@heidi/runtime/i18n';
// Shared mutation-key identity: the ownership ruling split this hook across
// two packages and both halves declare the same key, from one site.
import { trackPatientLinked } from '@/lib/analytics/scribe/patient';
import {
  LINK_SESSIONS_TO_PATIENT_PROFILE,
  type PatientProfile,
} from '@heidi/patients/contracts';
import { useMutateLinkOwnSessionsToPatientProfile as useMutateLinkOwnSessionsToPatientProfileCore } from '@heidi/patients/data';
import {
  GET_LINKED_SESSIONS_BY_PROFILE_ID,
  GET_SESSION_BY_ID,
  GET_TEAM_MEMBER_SESSION_BY_ID,
  GET_TEAM_MEMBER_SESSIONS_BY_PROFILE_ID,
  type QuerySessionResponse,
  type SessionsResponse,
} from '@heidi/session/contracts';
import { type InfiniteData, useMutation, useQueryClient } from '@tanstack/react-query';
import { produce } from 'immer';
import { useAtomValue } from 'jotai';
import { useCombinedQueriesTeamGeneralInfo } from '../query/useCombinedQueriesTeamGeneralInfo';
import { GET_TEAM_MEMBER_PATIENT_PROFILES } from '../query/useQueryPatientProfiles';
import { invalidatePatientRecap } from '../query/useQueryPatientRecap';
import { GET_SESSIONS, GET_TEAM_MEMBER_SESSIONS } from '../query/useQuerySessions';
import {
  GET_INFINITE_SESSIONS,
  GET_INFINITE_TEAM_MEMBER_SESSIONS,
  useGetPageSize,
} from '../query/useQuerySessionsV2';
import { useRBACPermissionCheck } from '../useRBACPermissionCheck';

/**
 * Optional funnel metadata for the `patient_linked` Snowplow event.
 * Populated by find-or-link callers (combobox surface + match method +
 * search_id stitching). Legacy callers (EHR launch, drawer, integrations,
 * chat) leave it undefined and emit the bare event.
 */
type PatientLinkedTrackingMetadata = Omit<
  PatientLinkedEventProps,
  'patient_id' | 'session_ids'
>;

type Props = {
  patientProfile: PatientProfile;
  sessionIds: string[];
  /** Passed as a query param for session direct-share permission check.
   *  When a session is directly shared, the sharee can process (create/update/link/unlink)
   *  patient profiles on that session by providing its ID here. */
  sessionId?: string;
  /**
   * Suppress the success toast for this call. Used by composite
   * operations (e.g. create-and-link) that own their own user-facing
   * toast — avoids showing the user two stacked toasts for one logical
   * action.
   *
   * NOTE: this is a bandage over a structural smell — mutation hooks
   * shouldn't own user-facing UX in the first place. The cleaner fix is
   * to remove the toast from this hook and have each call site decide,
   * but that touches ~8 unrelated callers (own + team-member variants
   * are both used by EHR launch, GDT, Epic, drawer, etc.) and is its
   * own PR. Tracked in `patient_linking_discussion.md` follow-ups.
   */
  suppressToast?: boolean;
  /**
   * Mark the patient recap invalid without refetching it now. Only for callers
   * that navigate away as this resolves — create-and-link pushes straight to a
   * note-less session, which the backend skips in its recency walk, so an
   * immediate refetch just pulls the same cached blurb back onto a surface that
   * is already unmounting. The next mount still refetches.
   *
   * Off by default: every other caller (find-or-link, bulk link, EHR launch,
   * GDT) can link a session that already carries a finalised note while a
   * recap surface stays mounted, and those need the refetch to land.
   */
  deferRecapRefetch?: boolean;
  /**
   * Optional analytics metadata spread into the `patient_linked` event in
   * onSuccess. Carries `surface`, `match_method`, `result_position`,
   * `result_count_seen`, `time_to_outcome_ms`, `search_id`, and
   * `feature_flag_variant` for the find-or-link combobox funnel; legacy
   * callers omit it.
   */
  tracking?: PatientLinkedTrackingMetadata;
};

function showLinkSessionSuccessToast(
  t: ReturnType<typeof useTranslations>['t'],
  input: Props
) {
  // Uses the design-system Sonner toast (5s default auto-dismiss).
  designSystemToast({
    tone: 'success',
    title: t(
      input.sessionIds.length === 1
        ? 'patient.sessionLinkedTo'
        : 'patient.sessionsLinkedTo',
      {
        name: input.patientProfile.demographic_string,
      }
    ),
    // Default toast width is 400px — shrink to content so short
    // messages don't leave empty space before the close button.
    className: 'w-auto',
  });
}

export function useMutateLinkSessionsToPatientProfile() {
  const { isReviewingOtherUser } = useRBACPermissionCheck();
  const isViewingOtherSession = useAtomValue(isViewingOtherSessionAtom);

  const linkOwnSessionsToPatientProfile = useMutateLinkOwnSessionsToPatientProfile();
  const linkTeamMemberSessionsToPatientProfile =
    useMutateLinkTeamMemberSessionsToPatientProfile();
  if (isViewingOtherSession) {
    return linkOwnSessionsToPatientProfile;
  }
  if (isReviewingOtherUser) {
    return linkTeamMemberSessionsToPatientProfile;
  }
  return linkOwnSessionsToPatientProfile;
}

/**
 * App-side delegate for `@heidi/patients/data` (D2.2, ARCFE-1053).
 *
 * Everything left here is Session's cache or a user-visible effect. The
 * optimistic session-list writes keep their own lifecycle position
 * (`onOptimisticLink` fires from the package's `onMutate`); the
 * linked-sessions invalidation, the sessions-list invalidation, the per-session
 * `GET_SESSION_BY_ID` patch + refetch, the toast and the `patient_linked`
 * analytics all fire from `onLinked`, in their original order relative to each
 * other.
 */
export function useMutateLinkOwnSessionsToPatientProfile() {
  const queryClient = useQueryClient();
  const { t } = useTranslations();
  const sessionQueryVersion = useAtomValue(sessionQueryVersionAtom);
  const isViewingOtherSession = useAtomValue(isViewingOtherSessionAtom);

  return useMutateLinkOwnSessionsToPatientProfileCore<PatientLinkedTrackingMetadata>({
    isViewingOtherSession,
    onOptimisticLink: (input) => {
      switch (sessionQueryVersion) {
        case 'v3':
        case 'v2':
          void queryClient.setQueriesData<InfiniteData<SessionsResponse>>(
            {
              queryKey: [
                sessionQueryVersion === 'v3'
                  ? GET_INFINITE_SESSIONS_WITH_PAGINATION
                  : GET_INFINITE_SESSIONS,
              ],
            },
            (oldData: InfiniteData<SessionsResponse> | undefined) => {
              if (!oldData) {
                return undefined;
              }
              return produce(oldData, (draft) => {
                const sessions = draft.pages
                  .flatMap((page) => page.sessions)
                  .filter((session) => input.sessionIds.includes(session.session_id));
                if (sessions) {
                  sessions.forEach((session) => {
                    session.patient_profile = {
                      ...input.patientProfile,
                    };
                  });
                }
              });
            }
          );
          break;
        default:
          void queryClient.setQueryData<SessionsResponse>([GET_SESSIONS], (oldData) => {
            if (!oldData) {
              return undefined;
            }
            return produce(oldData, (draft) => {
              const sessions = draft.sessions.filter((session) =>
                input.sessionIds.includes(session.session_id)
              );
              if (sessions) {
                sessions.forEach((session) => {
                  session.patient_profile = {
                    ...input.patientProfile,
                  };
                });
              }
            });
          });
      }
    },
    onLinked: (input) => {
      void queryClient.invalidateQueries({
        queryKey: [GET_LINKED_SESSIONS_BY_PROFILE_ID, input.patientProfile.id],
      });
      void invalidateSessionsQuery();

      input.sessionIds.forEach((sessionId) => {
        queryClient.setQueryData<QuerySessionResponse>(
          [GET_SESSION_BY_ID, { sessionId }],
          (oldData) => {
            if (oldData) {
              return {
                ...oldData,
                patient_profile: {
                  ...input.patientProfile,
                },
                session_name:
                  input.patientProfile.demographic_string || oldData.session_name,
              };
            } else {
              return oldData;
            }
          }
        );
        // Invalidate to refetch session with new fallback field
        void queryClient.invalidateQueries({
          queryKey: [GET_SESSION_BY_ID, { sessionId }],
        });
      });
      // Handle success case
      if (!input.suppressToast) {
        showLinkSessionSuccessToast(t, input);
      }
      // track link sessions to patient profile
      if (input.patientProfile.id) {
        trackPatientLinked({
          ...(input.tracking ?? {}),
          patient_id: input.patientProfile.id ?? '',
          session_ids: input.sessionIds,
        });
      }
    },
    onLinkFailed: () => {
      // The `logger.error(..., sanitizeError(error))` moved into the package.
      designSystemToast({
        tone: 'destructive',
        title: t('common.backendError'),
      });
    },
  });
}

export function useMutateLinkTeamMemberSessionsToPatientProfile() {
  const queryClient = useQueryClient();
  const { teamDetails } = useCombinedQueriesTeamGeneralInfo();
  const teamId = teamDetails?.team.id;
  const selectedReviewMember = useAtomValue(selectedReviewMemberAtom);
  const { t } = useTranslations();
  const pageSize = useGetPageSize();
  const sessionQueryVersion = useAtomValue(sessionQueryVersionAtom);
  return useMutation({
    mutationKey: [LINK_SESSIONS_TO_PATIENT_PROFILE],
    mutationFn: async (input: Props) => {
      if (input.patientProfile.id && teamId && selectedReviewMember) {
        return await postLinkSessionsToPatientProfile(
          teamId,
          selectedReviewMember,
          input.patientProfile.id,
          { session_ids: input.sessionIds }
        );
      }
    },

    onMutate: (input) => {
      if (sessionQueryVersion === 'v2' || sessionQueryVersion === 'v3') {
        void queryClient.setQueryData<InfiniteData<SessionsResponse>>(
          [GET_INFINITE_TEAM_MEMBER_SESSIONS, pageSize],
          (oldData) => {
            if (!oldData) {
              return undefined;
            }
            return produce(oldData, (draft) => {
              const sessions = draft.pages
                .flatMap((page) => page.sessions)
                .filter((session) => input.sessionIds.includes(session.session_id));
              if (sessions) {
                sessions.forEach((session) => {
                  session.patient_profile = {
                    ...input.patientProfile,
                  };
                });
              }
            });
          }
        );
      } else {
        void queryClient.setQueryData<SessionsResponse>(
          [GET_TEAM_MEMBER_SESSIONS, { teamId }, { memberId: selectedReviewMember }],
          (oldData) => {
            if (!oldData) {
              return undefined;
            }
            return produce(oldData, (draft) => {
              const sessions = draft.sessions.filter((session) =>
                input.sessionIds.includes(session.session_id)
              );
              if (sessions) {
                sessions.forEach((session) => {
                  session.patient_profile = {
                    ...input.patientProfile,
                  };
                });
              }
            });
          }
        );
      }
    },

    onSuccess: (_, input) => {
      logger.info('[PatientLink] Session linked to patient successfully');
      // invalidate team member linked to patient profile sessions query
      void queryClient.invalidateQueries({
        queryKey: [
          GET_TEAM_MEMBER_SESSIONS_BY_PROFILE_ID,
          input.patientProfile.id,
          teamId,
          selectedReviewMember,
        ],
      });
      // session_count on the team-member patient list is derived server-side;
      // linking a session bumps that count, so the cache must refetch.
      // The 4th key element matches `useQueryTeamMemberPatientProfiles` —
      // `isReviewingOtherUser` (always true on this code path).
      void queryClient.invalidateQueries({
        queryKey: [GET_TEAM_MEMBER_PATIENT_PROFILES, teamId, selectedReviewMember, true],
      });
      // Recap is keyed on profile id only (not team/member), so the same
      // invalidation refreshes the popover + drawer summary after linking.
      invalidatePatientRecap(
        queryClient,
        input.patientProfile.id,
        input.deferRecapRefetch ? 'none' : 'active'
      );
      // invalidate team member sessions query
      void invalidateTeamMemberSessionsQuery(teamId, selectedReviewMember);

      // update team member session by id
      input.sessionIds.forEach((sessionId) => {
        queryClient.setQueryData<QuerySessionResponse>(
          [
            GET_TEAM_MEMBER_SESSION_BY_ID,
            { teamId },
            { memberId: selectedReviewMember },
            { sessionId },
          ],
          (oldData) => {
            if (oldData) {
              return {
                ...oldData,
                patient_profile: {
                  ...input.patientProfile,
                },
                session_name:
                  input.patientProfile.demographic_string || oldData.session_name,
              };
            } else {
              return oldData;
            }
          }
        );
      });

      // Handle success case
      if (!input.suppressToast) {
        showLinkSessionSuccessToast(t, input);
      }
      // track link sessions to patient profile
      if (input.patientProfile.id) {
        trackPatientLinked({
          ...(input.tracking ?? {}),
          patient_id: input.patientProfile.id ?? '',
          session_ids: input.sessionIds,
        });
      }
    },
    onError: (error) => {
      logger.error(
        '[PatientLink] Failed to link session to patient',
        sanitizeError(error)
      );
      designSystemToast({
        tone: 'destructive',
        title: t('common.backendError'),
      });
    },
  });
}
